import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { LuLoaderCircle, LuTreePalm } from 'react-icons/lu';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import SegmentedControl from '../../components/ui/SegmentedControl';
import { HelpNote } from '../ProfilePage/components/ProfileUi';
import { getAnnualEntitlement, getCarryStartYear } from '../../utils/leaveBalance';
import { leaveTimeline, leaveYears, summarizeLeaves } from './leaveSummary';
import LeaveOverview from './parts/LeaveOverview';
import LeaveRecordList from './parts/LeaveRecordList';
import useSmoText from './smoText';

/**
 * A team member's leave (view only): annual-leave balance, how often and when
 * they took leave, and every request / entered leave with its details.
 * Records come from useTeamLeaves, so opening the window needs no new query.
 */
const EmployeeLeavesModal = ({ employee, records, loading, error, onHide }) => {
  const { t, label, language } = useSmoText();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  // Her yeni çalışanda bu yıldan başla
  useEffect(() => {
    setYear(currentYear);
  }, [employee?.id, currentYear]);

  const years = useMemo(() => leaveYears(records, currentYear).slice(0, 5), [records, currentYear]);
  const summary = useMemo(
    () => summarizeLeaves({ records, entitlement: getAnnualEntitlement(employee), carryFromYear: getCarryStartYear(employee), year }),
    [records, employee, year]
  );
  const timeline = useMemo(() => leaveTimeline(records), [records]);

  return (
    <Modal
      isOpen={!!employee}
      onClose={onHide}
      size="lg"
      title={label('leaves.title')}
      subtitle={employee ? `${employee.name || ''} ${employee.surname || ''}`.trim() : ''}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-teal-400 tw-to-emerald-600 tw-shadow-teal-500/30" size="xs">
          <LuTreePalm aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      actions={years.length > 1 ? (
        <SegmentedControl
          size="sm"
          layoutId="smo-leave-year"
          label={label('leaves.year')}
          value={year}
          onChange={setYear}
          options={years.map((y) => ({ value: y, label: String(y) }))}
          className="max-sm:tw-hidden"
        />
      ) : null}
      footer={<Button onClick={onHide}>{t('common.close')}</Button>}
    >
      <div className="tw-space-y-5">
        {years.length > 1 && (
          // Telefonda başlık dar; yıl seçimi içerikte
          <SegmentedControl
            size="sm"
            layoutId="smo-leave-year-phone"
            label={label('leaves.year')}
            value={year}
            onChange={setYear}
            options={years.map((y) => ({ value: y, label: String(y) }))}
            className="tw-max-w-full tw-overflow-x-auto sm:tw-hidden"
          />
        )}
        {loading ? (
          <p className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-8 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
            <LuLoaderCircle aria-hidden="true" className="tw-h-5 tw-w-5 tw-animate-spin tw-text-teal-500" />
            {label('leaves.loading')}
          </p>
        ) : error ? (
          <HelpNote tone="danger">{label('leaves.error')}</HelpNote>
        ) : (
          <>
            <LeaveOverview summary={summary} timeline={timeline} year={year} />
            <LeaveRecordList key={year} records={summary.records} year={year} />
          </>
        )}
        <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('leaves.help')}</p>
      </div>
    </Modal>
  );
};

EmployeeLeavesModal.propTypes = {
  employee: PropTypes.object,
  records: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
};

export default EmployeeLeavesModal;
